import {
  IonButton,
  IonContent,
  IonPage,
  IonImg,
  IonIcon,
  IonMenuToggle,
  IonInput,
  useIonLoading,
  useIonToast,
} from '@ionic/react';
import { ellipsisVertical } from 'ionicons/icons';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Auth } from 'aws-amplify';
import { firstValueFrom } from 'rxjs';
import { IState } from 'appRedux/createStore';
import { ReduxCommandCreator } from 'appRedux/actions';
import { AUTH_STATE } from 'appRedux/types/reduxTypes';
import {
  AuthService,
  IEmulatePlayerSearchRow,
  IUserInfo,
} from 'services/auth';
import AuthHelper from 'lib/auth.common';
import { groupsFromAccessToken, installAmplifySession } from 'lib/amplifySession';
import { S3_PROFILE_BASE as S3_BASE } from 'common/common';
import { saveState } from 'localStorage';
import { beginApplicationConfigLoginSession } from 'lib/applicationConfig';
import { markResumeLastViewedCasino } from 'lib/lastViewedCasino';
import { startSession } from 'lib/sessionExpiry';

const EMULATE_GROUP = 'slotcheck-enterprise-emulatePlayers';

const EpMenuKebab = () => (
  <div className="player-engagement__menu-fab">
    <IonMenuToggle menu="main">
      <IonButton fill="clear" className="player-engagement__menu-btn" aria-label="Open menu">
        <IonIcon icon={ellipsisVertical} className="player-engagement__menu-icon" />
      </IonButton>
    </IonMenuToggle>
  </div>
);

const EpMenuBack = () => (
  <IonMenuToggle menu="main">
    <IonButton
      fill="clear"
      className="player-engagement__back"
      aria-label="Open menu"
    >
      <IonImg src="assets/images/back-icon.svg" />
    </IonButton>
  </IonMenuToggle>
);

const ForbiddenCard = () => (
  <IonPage className="app-parent player-engagement emulate-players">
    <IonContent className="player-engagement emulate-players">
      <EpMenuKebab />
      <div className="player-engagement__content">
        <div className="player-engagement__breadcrumb-row">
          <EpMenuBack />
          <div className="player-engagement__breadcrumb">
            Emulate Players
          </div>
        </div>
        <div className="player-engagement__forbidden-card">
          <h2>403 · Emulate Players not enabled</h2>
          <p>
            Emulate Players requires the Cognito group{' '}
            <strong>{EMULATE_GROUP}</strong>. Ask ops to assign it for your account.
          </p>
        </div>
      </div>
    </IonContent>
  </IonPage>
);

const PlayerAvatar = ({ row }: { row: IEmulatePlayerSearchRow }) => {
  if (row.avatarId) {
    return (
      <div
        className="emulate-players__avatar-icon"
        style={{ backgroundImage: `url(${S3_BASE}/avatar-bg${Number(row.avatarBgId)}.png)` }}
      >
        <img
          src={`${S3_BASE}/avatar${Number(row.avatarId)}.png`}
          alt=""
          className="emulate-players__avatar-img"
        />
      </div>
    );
  }
  const initials = row.aliasInitial2 || row.aliasInitial || '';
  return (
    <div className="emulate-players__avatar-initials">
      <span>{initials}</span>
    </div>
  );
};

function httpStatus(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const e = err as { response?: { status?: number }; status?: number };
  return e.response?.status ?? e.status;
}

export const EmulatePlayersPage = () => {
  const dispatch = useDispatch();
  const store = useStore<IState>();
  const commands = ReduxCommandCreator(dispatch);
  const [presentToast] = useIonToast();
  const [presentLoading, dismissLoading] = useIonLoading();
  const authService = useMemo(() => new AuthService(), []);

  const groups =
    (useSelector<IState>((s) => s.app.persistedState.userInfo?.groups) as string[]) || [];
  const canAccess = groups.includes(EMULATE_GROUP);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<IEmulatePlayerSearchRow[]>([]);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emulatingPk, setEmulatingPk] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLIonInputElement | null>(null);

  const runSearch = useCallback(async () => {
    // Prefer the live input value — onIonChange alone can lag behind typing
    // when Search is clicked before blur commits the controlled state.
    let live = query;
    try {
      const native = await searchInputRef.current?.getInputElement();
      if (native && typeof native.value === 'string') {
        live = native.value;
      }
    } catch {
      /* keep state value */
    }
    const q = live.trim();
    setQuery(q);
    if (!q || !canAccess) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    try {
      const res = await firstValueFrom(authService.SearchPlayersForEmulate(q));
      // Prefer response.players; tolerate alternate envelopes from proxies.
      const envelope = (res as { response?: { players?: unknown }; players?: unknown }) || {};
      const raw =
        envelope.response && Array.isArray(envelope.response.players)
          ? envelope.response.players
          : Array.isArray(envelope.players)
            ? envelope.players
            : [];
      setResults(raw as IEmulatePlayerSearchRow[]);
    } catch (err: unknown) {
      setResults([]);
      const status = httpStatus(err);
      void presentToast({
        position: 'top',
        duration: 4000,
        color: 'danger',
        message:
          status === 403
            ? 'Emulate Players is not enabled for this account.'
            : 'Search failed. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  }, [authService, canAccess, presentToast, query]);

  const onEmulate = useCallback(
    async (row: IEmulatePlayerSearchRow) => {
      if (!canAccess || emulatingPk) return;
      setEmulatingPk(row.appUserPk);
      await presentLoading({ message: 'Emulating…', cssClass: 'custom-loading' });
      try {
        // 1) Mint while staff session is still valid.
        const mintRes = await firstValueFrom(authService.EmulatePlayer(row.appUserPk));
        const tokens = mintRes?.response;
        if (!tokens?.idToken || !tokens?.accessToken || !tokens?.refreshToken) {
          throw new Error('Emulate response missing tokens');
        }

        // 2) Destroy staff Amplify session. Do not keep a staff refresh token.
        try {
          await Auth.signOut();
        } catch {
          /* already signed out */
        }
        AuthHelper.ClearToken();
        AuthHelper.ClearJWKToken();

        // 3) Install player Amplify session so currentSession() is the player.
        installAmplifySession({
          idToken: tokens.idToken,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
        });

        // 4) Existing Login with the new ID token → HS256 Token + user payload.
        const loginResponse = await firstValueFrom(authService.Login());
        if (!loginResponse?.response?.user || !loginResponse.response.token) {
          throw new Error('Login after Emulate failed');
        }

        const user = loginResponse.response.user;
        const playerGroups = groupsFromAccessToken(tokens.accessToken);
        const cell = String(user.cellNumber || user.phone || '').replace(/^\+/, '');
        const lastLogin = loginResponse.response.lastLogin;
        const last_login_timestamp_counter = lastLogin?.last_login_timestamp_counter;
        const showUserConfirm =
          !last_login_timestamp_counter || last_login_timestamp_counter < 1;

        beginApplicationConfigLoginSession();
        commands.SetAuthState(AUTH_STATE.AUTHENTICATED);
        AuthHelper.SetToken(loginResponse.response.token);
        AuthHelper.SetJWKToken(tokens.idToken);
        // Inactivity logout: the emulated player's own rule (4h only with a verified link).
        startSession(Date.now(), user.linkedAccounts);

        commands.InitUserInfo(
          cell,
          cell,
          user.emailAddress as string,
          user.appUserPk,
          playerGroups
        );
        commands.SetTourState(Boolean(user.hasCompletedOnboarding));
        commands.SetUserAlias({
          aliasInitial: user.aliasInitial,
          aliasInitial2: user.aliasInitial2,
          aliasName: user.aliasName,
        } as Partial<IUserInfo>);
        commands.SetUserInformation({
          firstName: user.firstName,
          lastName: user.lastName,
          emailAddress: user.emailAddress,
          isCasinoAccount: user.isCasinoAccount,
          // The emulated player's own Slot Check Search access (Login returns the full user).
          slotCheckSearchEnabled: user.slotCheckSearchEnabled ?? null,
        } as Partial<IUserInfo>);
        if (typeof user.avatarId === 'number') {
          commands.SetUserAvatar(user.avatarId, user.avatarBgId ?? 0);
        }
        commands.SetUserConfirm(showUserConfirm);

        // Persist immediately — store.subscribe saveState is throttled to 1s.
        saveState(store.getState().app.persistedState);

        await dismissLoading();
        // Resume the *emulated* user's last-viewed casino (their users_casinos).
        markResumeLastViewedCasino();
        // Hard-navigate: same post-login branch as a normal player login.
        window.location.replace(
          showUserConfirm ? '/auth/userconfirm' : '/auth/choosecasinos'
        );
      } catch (err: unknown) {
        await dismissLoading();
        setEmulatingPk(null);
        const status = httpStatus(err);
        void presentToast({
          position: 'top',
          duration: 5000,
          color: 'danger',
          message:
            status === 403
              ? 'Emulate is not allowed for this account.'
              : 'Emulate failed. Please try again.',
        });
      }
    },
    [
      authService,
      canAccess,
      commands,
      dismissLoading,
      emulatingPk,
      presentLoading,
      presentToast,
      store,
    ]
  );

  if (!canAccess) {
    return <ForbiddenCard />;
  }

  return (
    <IonPage className="app-parent player-engagement emulate-players">
      <IonContent className="player-engagement emulate-players">
        <EpMenuKebab />
        <div className="player-engagement__content">
          <div className="player-engagement__breadcrumb-row">
            <EpMenuBack />
            <div className="player-engagement__breadcrumb">
              <span>Emulate Players</span>
            </div>
          </div>

          <div className="player-engagement__header">
            <div className="player-engagement__title-row">
              <h1 className="player-engagement__title">Emulate Players</h1>
            </div>
            <p className="emulate-players__subtitle">
              Search by phone number or alias, then Emulate to enter that player&apos;s session.
            </p>
          </div>

          <form
            className="emulate-players__search"
            onSubmit={(e) => {
              e.preventDefault();
              void runSearch();
            }}
          >
            <IonInput
              ref={searchInputRef}
              className="emulate-players__input"
              value={query}
              placeholder="Phone number or alias"
              onIonInput={(e) => {
                const raw = (e.target as HTMLIonInputElement | null)?.value;
                setQuery(String(raw ?? ''));
              }}
              onIonChange={(e) => setQuery(String(e.detail.value ?? ''))}
              aria-label="Search players"
            />
            <IonButton
              type="submit"
              className="emulate-players__search-btn"
              disabled={loading || !query.trim()}
            >
              {loading ? 'Searching…' : 'Search'}
            </IonButton>
          </form>

          <div className="emulate-players__results" role="list">
            {searched && !loading && results.length === 0 && (
              <p className="emulate-players__empty">No players found.</p>
            )}
            {results.map((row) => (
              <div className="emulate-players__row" role="listitem" key={row.appUserPk}>
                <PlayerAvatar row={row} />
                <div className="emulate-players__alias">{row.aliasName}</div>
                <IonButton
                  className="emulate-players__emulate-btn"
                  disabled={emulatingPk !== null}
                  onClick={() => void onEmulate(row)}
                >
                  {emulatingPk === row.appUserPk ? 'Emulating…' : 'Emulate'}
                </IonButton>
              </div>
            ))}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default EmulatePlayersPage;
